export interface IInquiryRecord {
  id: string;
  /** Display column; same value as `id` from API `leadInquiryId`. */
  leadInquiryId?: string;
  candidateId: number;
  candidateName: string;
  recordType: 'inquiry' | 'engagement' | 'referral' | 'application';
  /** Optional; profile Leads tab still shows inquiry name when present. */
  inquiryName?: string;
  brandId?: string;
  brand: string;
  discipline: string;
  leadOrigin: string;
  leadSourceCategory: string;
  ownerName: string;
  externalInquiryId?: string;
  jobId?: number | null;
  orderId?: number | null;
  createdDate: string;
  lastUpdatedDate: string;
}

/** Empty-cell placeholder aligned with other AMIE data grids (e.g. margin search). */
export const engagementGridEmptyDisplay = '--';

export function formatEngagementGridCell(value: unknown): string {
  if (value === null || value === undefined) {
    return engagementGridEmptyDisplay;
  }
  if (typeof value === 'number' && Number.isNaN(value)) {
    return engagementGridEmptyDisplay;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return engagementGridEmptyDisplay;
  }
  return String(value);
}

export const formatIsoToUsDate = (value?: string): string => {
  if (!value) {
    return '';
  }

  const datePart = value.split('T')[0];
  const [year, month, day] = datePart.split('-');

  if (!year || !month || !day) {
    return '';
  }

  return `${month}/${day}/${year}`;
};

export const parseUsDate = (value?: string): Date | null => {
  if (!value) {
    return null;
  }
  const [month, day, year] = value.split('/').map(part => Number(part));
  if (!month || !day || !year) {
    return null;
  }
  // Use explicit UTC construction so parsed row dates align with the UTC-split
  // display strings produced by formatIsoToUsDate, preventing off-by-one in dateInRange.
  return new Date(Date.UTC(year, month - 1, day));
};

export const dateInRange = (
  value: string,
  range?: { startDate?: Date; endDate?: Date } | null,
): boolean => {
  if (!range?.startDate && !range?.endDate) {
    return true;
  }
  const rowDate = parseUsDate(value);
  if (!rowDate) {
    return false;
  }
  // Normalise the picker-provided filter dates to UTC midnight so comparisons
  // are on the same UTC basis as the row dates from parseUsDate.
  const startDate = range?.startDate
    ? new Date(Date.UTC(range.startDate.getFullYear(), range.startDate.getMonth(), range.startDate.getDate()))
    : null;
  const endDate = range?.endDate
    ? new Date(Date.UTC(range.endDate.getFullYear(), range.endDate.getMonth(), range.endDate.getDate()))
    : null;
  if (startDate && rowDate < startDate) {
    return false;
  }
  if (endDate && rowDate > endDate) {
    return false;
  }
  return true;
};
